import { useEffect, useMemo, useRef, useState } from "react";

import { formatTicketTabBadgeCount, formatUnreadMessagesBadgeCount } from "../../helpers/countUnreadConversations";
import { isStatusMessage, isStatusTicketRecord } from "../../helpers/isStatusTicket";
import { isSameTicketCompany, ticketStatusSocketRoom } from "../../helpers/ticketSocketRoom";
import { TICKET_REALTIME } from "../../helpers/ticketRealtimeEvents";
import {
	addGoWebSocketMessageHandler,
	addGoWebSocketOpenHandler,
	connectSocket,
	getGoWebSocket,
} from "../../services/socketv2";
import useTickets from "../useTickets";

const TAB_STATUSES = ["open", "pending"];
const FOCUS_SYNC_COOLDOWN_MS = 15_000;
const RECONCILE_AFTER_LOCAL_MS = 3_000;

function parseJsonList(raw) {
	if (!raw) return [];
	try {
		const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

// Espelha buildTicketsQuery (Go) para o socket não contar tickets que a API não devolve.
function ticketMatchesBadgeFilters(
	ticket,
	{ queueIds, channels, userId, userQueueIds, canSeeAll, canSeeGroups, showAllOpen }
) {
	if (!ticket) return false;
	if (isStatusTicketRecord(ticket)) return false;

	const selected = queueIds.map(Number).filter((n) => !Number.isNaN(n));
	const effectiveQueueIds = selected.length > 0 ? selected : userQueueIds;
	if (effectiveQueueIds.length > 0) {
		if (ticket.queueId == null) {
			if (!canSeeAll) return false;
		} else if (!effectiveQueueIds.includes(Number(ticket.queueId))) {
			return false;
		}
	}

	const status = String(ticket.status || "").toLowerCase();
	if (status === "open" && !showAllOpen && !canSeeAll) {
		if (Number(ticket.userId) !== Number(userId)) return false;
	}

	if (!canSeeGroups && ticket.isGroup) return false;

	if (channels.length > 0 && ticket.channel && !channels.includes(ticket.channel)) {
		return false;
	}

	return true;
}

/**
 * Badges open/pending:
 * - Fonte coletiva: socket Go (update com previousStatus)
 * - Otimismo local: TICKET_REALTIME (quem clicou)
 * - Correção: API countOnly (debounce após ação local / ao focar a aba)
 * - Sem polling periódico (removido PERIODIC_SYNC_MS)
 */
const useTicketTabBadgeCounts = ({
	userId,
	companyId,
	showAllTickets,
	queueIdsParam,
	tagsIdsParam,
	channelsParam,
	userQueues,
	canSeeAllTickets,
	canSeeGroups,
}) => {
	const queueIds = useMemo(() => parseJsonList(queueIdsParam), [queueIdsParam]);
	const channels = useMemo(() => parseJsonList(channelsParam), [channelsParam]);
	const userQueueIdsKey = (userQueues || []).map((q) => Number(q?.id)).filter(Boolean).join(",");
	const userQueueIds = useMemo(
		() => (userQueueIdsKey ? userQueueIdsKey.split(",").map(Number) : []),
		[userQueueIdsKey]
	);

	const [focusSyncTick, setFocusSyncTick] = useState(0);
	const lastFocusSyncRef = useRef(0);
	const ticketMetaRef = useRef(new Map());
	const appliedTransitionRef = useRef(new Set());
	const reconcileTimerRef = useRef(null);

	const [openCount, setOpenCount] = useState(0);
	const [pendingCount, setPendingCount] = useState(0);
	const [openUnreadSum, setOpenUnreadSum] = useState(0);
	const [openHasMore, setOpenHasMore] = useState(false);
	const [pendingHasMore, setPendingHasMore] = useState(false);

	const { count: openApiCount, hasMore: openApiHasMore } = useTickets({
		pageNumber: "1",
		status: "open",
		showAll: showAllTickets,
		queueIds: queueIdsParam,
		tagsIds: tagsIdsParam,
		countOnly: "true",
		channels: channelsParam,
		badgeRefresh: focusSyncTick,
	});

	const { count: pendingApiCount, hasMore: pendingApiHasMore } = useTickets({
		pageNumber: "1",
		status: "pending",
		showAll: "true",
		queueIds: queueIdsParam,
		tagsIds: tagsIdsParam,
		countOnly: "true",
		channels: channelsParam,
		badgeRefresh: focusSyncTick,
	});

	const { count: openUnreadTicketsApiCount } = useTickets({
		pageNumber: "1",
		status: "open",
		showAll: showAllTickets,
		queueIds: queueIdsParam,
		tagsIds: tagsIdsParam,
		unreadBadge: "true",
		channels: channelsParam,
		badgeRefresh: focusSyncTick,
	});

	const applyApiBaseline = (
		openC,
		pendingC,
		unreadTicketsC,
		openMore,
		pendingMore
	) => {
		setOpenCount(Number(openC) || 0);
		setPendingCount(Number(pendingC) || 0);
		setOpenUnreadSum(Number(unreadTicketsC) || 0);
		setOpenHasMore(Boolean(openMore));
		setPendingHasMore(Boolean(pendingMore));
		ticketMetaRef.current.clear();
		appliedTransitionRef.current.clear();
	};

	useEffect(() => {
		applyApiBaseline(
			openApiCount,
			pendingApiCount,
			openUnreadTicketsApiCount,
			openApiHasMore,
			pendingApiHasMore
		);
	}, [
		openApiCount,
		pendingApiCount,
		openUnreadTicketsApiCount,
		openApiHasMore,
		pendingApiHasMore,
		queueIdsParam,
		tagsIdsParam,
		channelsParam,
		showAllTickets,
		focusSyncTick,
	]);

	useEffect(() => {
		if (!userId) return undefined;

		const filters = {
			queueIds,
			channels,
			userId,
			userQueueIds,
			canSeeAll: Boolean(canSeeAllTickets),
			canSeeGroups: Boolean(canSeeGroups),
			showAllOpen: showAllTickets === "true",
		};

		const scheduleReconcile = () => {
			if (reconcileTimerRef.current) {
				clearTimeout(reconcileTimerRef.current);
			}
			reconcileTimerRef.current = setTimeout(() => {
				reconcileTimerRef.current = null;
				lastFocusSyncRef.current = Date.now();
				setFocusSyncTick((n) => n + 1);
			}, RECONCILE_AFTER_LOCAL_MS);
		};

		const remember = (ticket) => {
			if (!ticket?.id) return;
			ticketMetaRef.current.set(Number(ticket.id), {
				status: String(ticket.status || "").toLowerCase(),
				unread: Number(ticket.unreadMessages) || 0,
			});
		};

		const adjustStatusCount = (status, delta) => {
			const s = String(status || "").toLowerCase();
			if (s === "open") {
				setOpenCount((c) => Math.max(0, (Number(c) || 0) + delta));
				scheduleReconcile();
			} else if (s === "pending") {
				setPendingCount((c) => Math.max(0, (Number(c) || 0) + delta));
				scheduleReconcile();
			}
		};

		const unreadTicketDelta = (oldUnread, nextUnread) => {
			const wasUnread = (Number(oldUnread) || 0) > 0;
			const isUnread = (Number(nextUnread) || 0) > 0;
			if (wasUnread === isUnread) return 0;
			return isUnread ? 1 : -1;
		};

		const adjustUnread = (delta) => {
			if (!delta) return;
			setOpenUnreadSum((c) => Math.max(0, (Number(c) || 0) + delta));
			scheduleReconcile();
		};

		const applyStatusTransition = (fromStatus, toStatus, nextUnread = 0, oldUnread = 0) => {
			const from = String(fromStatus || "").toLowerCase();
			const to = String(toStatus || "").toLowerCase();
			if (!to || from === to) return false;

			if (from === "open" || from === "pending") adjustStatusCount(from, -1);
			if (to === "open" || to === "pending") adjustStatusCount(to, 1);
			// Conta tickets com não lidas, não a soma de mensagens.
			if (from === "open" && (Number(oldUnread) || 0) > 0) adjustUnread(-1);
			if (to === "open" && (Number(nextUnread) || 0) > 0) adjustUnread(1);
			return true;
		};

		/**
		 * Aplica pending/open/closed uma vez por transição.
		 * previousStatus do Go (ou do REALTIME local) é a chave para todo mundo atualizar.
		 */
		const tryApplyTransition = (ticket, hintedPrev, allowPendingFallback) => {
			if (!ticket || !ticketMatchesBadgeFilters(ticket, filters)) return;
			if (isStatusTicketRecord(ticket)) return;

			const id = Number(ticket.id);
			if (!id) return;

			const nextStatus = String(ticket.status || "").toLowerCase();
			const nextUnread = Number(ticket.unreadMessages) || 0;
			const prev = ticketMetaRef.current.get(id);
			const hinted =
				hintedPrev != null && String(hintedPrev).trim() !== ""
					? String(hintedPrev).toLowerCase()
					: null;

			let from = hinted || prev?.status || null;
			// Só quem clicou (REALTIME) pode assumir pending→open sem hint
			if (!from && allowPendingFallback && nextStatus === "open") {
				from = "pending";
			}
			const to = nextStatus;
			const oldUnread = prev?.unread || 0;

			if (!from || !to || from === to) {
				remember(ticket);
				return;
			}

			const key = `${id}:${from}->${to}`;
			if (appliedTransitionRef.current.has(key)) {
				remember(ticket);
				return;
			}

			applyStatusTransition(from, to, nextUnread, oldUnread);
			appliedTransitionRef.current.add(key);
			appliedTransitionRef.current.delete(`${id}:${to}->${from}`);

			if (to === "closed") {
				ticketMetaRef.current.delete(id);
			} else {
				remember(ticket);
			}
		};

		const applyRealtimeStatusEvent = (data) => {
			const ticket = data?.ticket;
			if (!ticket) return;
			// delete local é só lista visual
			if (data.action === "delete") return;
			if (data.action !== "update") return;
			tryApplyTransition(ticket, data.previousStatus, true);
		};

		const applySocketEvent = (data) => {
			if (!data || data._fromRealtime) return;
			const action = data.action;
			const ticket = data.ticket;

			if (ticket && !isSameTicketCompany(ticket, companyId)) return;
			if (ticket && isStatusTicketRecord(ticket)) return;
			if (data.message && isStatusMessage(data.message) && !ticket) return;

			// delete do Go = lista; badge usa update+previousStatus
			if (action === "delete") return;

			if (action === "create" && data.message && ticket) {
				if (!ticketMatchesBadgeFilters(ticket, filters)) return;
				const status = String(ticket.status || "").toLowerCase();
				const id = Number(ticket.id);
				const prev = ticketMetaRef.current.get(id);
				if (status !== "open") {
					remember(ticket);
					return;
				}
				if (data.message.fromMe) {
					// Sem meta local: assume que saiu de "não lido" (API/reconcile corrige).
					const prevUnread = prev ? prev.unread : 1;
					adjustUnread(unreadTicketDelta(prevUnread, 0));
					remember({ ...ticket, unreadMessages: 0 });
					return;
				}
				const prevUnread = prev ? prev.unread : 0;
				const nextUnread =
					ticket.unreadMessages != null && !Number.isNaN(Number(ticket.unreadMessages))
						? Number(ticket.unreadMessages)
						: Math.max(1, prevUnread + 1);
				adjustUnread(unreadTicketDelta(prevUnread, nextUnread));
				remember({ ...ticket, unreadMessages: nextUnread });
				return;
			}

			if (action === "create" && ticket && !data.message) {
				if (!ticketMatchesBadgeFilters(ticket, filters)) return;
				const status = String(ticket.status || "").toLowerCase();
				const id = Number(ticket.id);
				if (ticketMetaRef.current.has(id)) return;
				if (status === "pending" || status === "open") {
					adjustStatusCount(status, 1);
					if (status === "open" && (Number(ticket.unreadMessages) || 0) > 0) {
						adjustUnread(1);
					}
				}
				remember(ticket);
				return;
			}

			if (action === "updateUnread" && ticket) {
				if (!ticketMatchesBadgeFilters(ticket, filters)) return;
				const id = Number(ticket.id);
				const prev = ticketMetaRef.current.get(id);
				const nextUnread = Number(ticket.unreadMessages) || 0;
				const status = String(ticket.status || prev?.status || "").toLowerCase();
				if (status === "open") {
					// Sem meta: se zerou, decrementa 1 ticket; se ganhou unread, incrementa 1.
					const oldUnread = prev ? prev.unread : nextUnread > 0 ? 0 : 1;
					adjustUnread(unreadTicketDelta(oldUnread, nextUnread));
				}
				remember({ ...ticket, unreadMessages: nextUnread });
				return;
			}

			// Update de status do Go (aceite/fechar/devolver) — com previousStatus
			if (action === "update" && ticket) {
				const id = Number(ticket.id);
				const prev = ticketMetaRef.current.get(id);

				if (!ticketMatchesBadgeFilters(ticket, filters)) {
					// Saiu da visibilidade (transferência de setor/atendente): a API recalcula.
					if (prev) {
						ticketMetaRef.current.delete(id);
						scheduleReconcile();
					}
					return;
				}

				// updates de mensagem (appMessage) não devem mover aba
				if (data.message) {
					remember(ticket);
					return;
				}
				const nextStatus = String(ticket.status || "").toLowerCase();
				const hasStatusHint =
					data.previousStatus != null &&
					String(data.previousStatus).trim() !== "" &&
					String(data.previousStatus).toLowerCase() !== nextStatus;
				const statusChanged = prev && prev.status && prev.status !== nextStatus;

				if (hasStatusHint || statusChanged) {
					tryApplyTransition(ticket, data.previousStatus, false);
					return;
				}

				if (nextStatus === "open") {
					const oldUnread = prev ? prev.unread : 0;
					const nextUnread = Number(ticket.unreadMessages) || 0;
					adjustUnread(unreadTicketDelta(oldUnread, nextUnread));
				}
				remember(ticket);
			}
		};

		connectSocket({ userId, companyId });

		const joinGoTabRooms = () => {
			const s = getGoWebSocket();
			if (!s || s.readyState !== WebSocket.OPEN) return;
			// Só salas de status + notification da empresa.
			// NÃO entrar em company-{id}: floodava todos os setores no TicketsList (WS compartilhado).
			s.send(JSON.stringify({ action: "joinTickets", room: "notification" }));
			TAB_STATUSES.forEach((tabStatus) => {
				const room = ticketStatusSocketRoom(companyId, tabStatus);
				if (room) s.send(JSON.stringify({ action: "joinTickets", room }));
			});
		};

		const handleGoMessage = (event) => {
			try {
				applySocketEvent(JSON.parse(event.data));
			} catch {
				/* ignore */
			}
		};

		const removeGoMessage = addGoWebSocketMessageHandler(handleGoMessage);
		const removeGoOpen = addGoWebSocketOpenHandler(joinGoTabRooms);
		joinGoTabRooms();

		const onTicketRealtime = (event) => {
			const detail = event?.detail;
			if (!detail) return;
			applyRealtimeStatusEvent({ ...detail, _fromRealtime: true });
		};
		window.addEventListener(TICKET_REALTIME, onTicketRealtime);

		const onVisibility = () => {
			if (document.visibilityState !== "visible") return;
			const now = Date.now();
			if (now - lastFocusSyncRef.current < FOCUS_SYNC_COOLDOWN_MS) return;
			lastFocusSyncRef.current = now;
			setFocusSyncTick((n) => n + 1);
		};
		document.addEventListener("visibilitychange", onVisibility);

		return () => {
			if (reconcileTimerRef.current) {
				clearTimeout(reconcileTimerRef.current);
			}
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener(TICKET_REALTIME, onTicketRealtime);
			removeGoMessage();
			removeGoOpen();
		};
	}, [
		userId,
		companyId,
		queueIds,
		channels,
		userQueueIds,
		canSeeAllTickets,
		canSeeGroups,
		showAllTickets,
	]);

	return {
		openCount: formatTicketTabBadgeCount(openCount, openHasMore),
		pendingCount: formatTicketTabBadgeCount(pendingCount, pendingHasMore),
		openUnreadMessagesSum: formatUnreadMessagesBadgeCount(openUnreadSum),
	};
};

export default useTicketTabBadgeCounts;
